import { api, ApiError } from './api';
import { askConfirm } from '../components/dialogs';

// Dva lidé v jednom záznamu: posílá se jen to, co člověk opravdu změnil, takže cizí změny
// jiných polí zůstanou. Když kolega změnil totéž pole, server to odmítne a rozhodne člověk.
export class SaveCancelled extends Error {}

export interface FormEdit<V> { start: V; record: object }

const LABELS: Record<string, string> = {
  name: 'Název', subtitle: 'Podnázev', description: 'Popis', notes: 'Poznámka', quantity: 'Množství', status: 'Stav',
  deadline: 'Termín', clientId: 'Odběratel', departmentId: 'Department', currency: 'Měna', storageUrl: 'Sdílená složka',
  type: 'Typ', assignedToUserId: 'Přiřazený člověk', plannedStart: 'Začátek', plannedEnd: 'Konec',
  estimatedMinutes: 'Odhad času', estimatedCost: 'Odhad nákladu', flatLaborCost: 'Paušál', sku: 'SKU', category: 'Kategorie',
  unit: 'Jednotka', minQty: 'Minimum', pricePerUnit: 'Cena', supplier: 'Dodavatel', location: 'Umístění', barcode: 'Čárový kód',
  locationId: 'Pozice', contactPerson: 'Kontakt', email: 'E-mail', phone: 'Telefon', updatedAt: 'celý záznam',
};

const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

export function changedFields(payload: object, start: object, record: object) {
  const now = payload as Record<string, unknown>;
  const before = start as Record<string, unknown>;
  const saved = record as Record<string, unknown>;
  const data: Record<string, unknown> = {};
  const base: Record<string, unknown> = {};
  for (const key of Object.keys(now)) {
    if (same(now[key], before[key])) continue;
    data[key] = now[key] ?? null; // prázdné pole = vymazat
    base[key] = saved[key] ?? null;
  }
  return { data, base };
}

function conflictFields(err: unknown): string[] | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  const fields = (err.details as { conflict?: string[] } | undefined)?.conflict;
  return fields?.length ? fields : null;
}

export async function patchChecked<T>(path: string, data: object, base: object): Promise<T> {
  try {
    return await api.patch<T>(path, { ...data, _base: base });
  } catch (err) {
    const fields = conflictFields(err);
    if (!fields) throw err;
    const overwrite = await askConfirm({
      title: 'Mezitím to změnil někdo jiný',
      body: `Týká se: ${fields.map((f) => LABELS[f] ?? f).join(', ')}. Tvoje verze tu změnu přepíše.`,
      confirmLabel: 'Přepsat',
      cancelLabel: 'Zpět k úpravám',
      danger: true,
    });
    if (!overwrite) throw new SaveCancelled();
    return api.patch<T>(path, data);
  }
}

export async function patchSafely<T>(path: string, payload: object, edit?: { start: object; record: object }): Promise<T> {
  if (!edit) return api.patch<T>(path, payload);
  const { data, base } = changedFields(payload, edit.start, edit.record);
  if (Object.keys(data).length === 0) return edit.record as T;
  return patchChecked<T>(path, data, base);
}
